import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { API } from '@/api'
import {
  Button,
  ButtonRadio,
  Divider,
  Loading,
  message,
  Modal,
  Pagination,
  Trans,
} from '@/components'
import { CollapseTableRow } from '@/components/CollapseTableRow.tsx'
import { EmptyView } from '@/components/EmptyView'
import { ScaleSpec } from '@/models/scaleSpec.ts'
import ScalingSpecDetail from '@/pages/auto-scaling/components/ScalingSpecDetail.tsx'
import { formatPrice } from '@/utils/formatter.ts'
import { cn } from '@/utils/style.ts'

interface FilterItem {
  label: string
  value: string
  codes: boolean
}

export function AutoScalingListPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [scaleSpecList, setScaleSpecList] = useState<ScaleSpec[]>([])
  const pageSize = 10
  const { t } = useTranslation()

  const statusFilters: FilterItem[] = [
    {
      label: t('scaling.type.running'),
      value: 'running',
      codes: true,
    },
    {
      label: t('scaling.type.suspended'),
      value: 'suspended',
      codes: false,
    },
  ]
  const [filter, setFilter] = useState<FilterItem | undefined>(statusFilters[0])

  const getScaleSpecList = useCallback(
    async (p = page) => {
      setLoading(true)
      try {
        const res = await API.getScaleSpecList({
          page: p,
          per_page: pageSize,
          enabled: filter?.codes,
        })
        if (res.code === 0) {
          setPage(res.pagination.page)
          setTotal(res.pagination.total)
          setScaleSpecList(res.list.map((spec) => new ScaleSpec(spec)))
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [page, pageSize, filter],
  )

  const onToggleScaleSpec = (spec: ScaleSpec) => {
    API.toggleScaleSpec({ spec_id: spec.resID, enabled: !spec.enabled }).then(() => {
      getScaleSpecList(page)
      message.success(t('common.message.success'))
    })
  }

  const onDeleteScaleSpec = (spec: ScaleSpec) => {
    Modal.confirm({
      title: t('scaling.deleteModal.title'),
      content: (
        <div className="bg-panel-gray-100 rounded-lg p-4 flex-row">
          <div className="t-sm font-medium text-secondary flex items-center">
            <Trans i18nKey="scaling.deleteModal.content">
              You are about to delete this rule permanently. Are you sure?
            </Trans>
          </div>
        </div>
      ),
      confirmBtnCfg: {
        type: 'danger',
        text: t('scaling.deleteModal.confirmBtn'),
      },
      className: 'w-[640px]',
    }).then(() => {
      API.deleteScaleSpec({ spec_id: spec.resID }).then(() => {
        message.success(t('common.message.success'))
        getScaleSpecList()
      })
    })
  }

  useEffect(() => {
    getScaleSpecList(1)
  }, [])

  return (
    <div className="card bg-white h-full overflow-y-auto">
      <div className="flex items-start justify-between mb-4">
        <div className="h6">
          <Trans i18nKey="scaling.title">Autoscaling</Trans>
        </div>
        <Link to="/scaling/create">
          <Button type="secondary">
            <Trans i18nKey="scaling.createBtn">New Scaling Rule</Trans>
          </Button>
        </Link>
      </div>

      <Divider />

      <ButtonRadio
        value={filter?.value}
        items={statusFilters}
        onChange={(e) => {
          setFilter(statusFilters.find((i) => i.value === e))
        }}
      />

      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}

      {!loading && scaleSpecList.length > 0 && (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={2}>
                <Trans i18nKey="scaling.tableHeaderID">ID</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="scaling.tableHeaderName">Name</Trans>
              </th>
              <th colSpan={1}>
                <Trans i18nKey="scaling.tableHeaderStatus">Status</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="scaling.tableHeaderCost">Cost</Trans>
              </th>
              <th colSpan={3}>
                <Trans i18nKey="scaling.tableHeaderLastExeTime">Last Execution Time</Trans>
              </th>
              <th colSpan={3}>
                <Trans i18nKey="scaling.tableHeaderAction">Action</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {scaleSpecList.map((spec) => (
              <CollapseTableRow
                key={spec.resID}
                colSpan={13}
                tableClassName={'!border-none'}
                content={<ScalingSpecDetail scalingSpecResId={spec.resID} />}
              >
                <td colSpan={2} className="t-sm">
                  {spec.resID}
                </td>
                <td colSpan={2}>{spec.class}</td>
                <td colSpan={1}>{filter?.label}</td>
                <td colSpan={2}>{formatPrice(0)}</td>
                <td colSpan={3}>{dayjs(spec.createTime).format('YYYY-MM-DD hh:mm:ss')}</td>
                <td colSpan={2}>
                  <div className="flex gap-x-4">
                    <div
                      className={cn('t-sb text-highlight cursor-pointer')}
                      onClick={() => {
                        navigate('/scaling/create', { state: { editingSpecResId: spec.resID } })
                      }}
                    >
                      <Trans i18nKey="scaling.action.edit">Edit</Trans>
                    </div>
                    <div
                      className={cn('t-sb text-highlight cursor-pointer')}
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleScaleSpec(spec)
                      }}
                    >
                      {spec.enabled ? (
                        <Trans i18nKey="scaling.action.suspend">Suspend</Trans>
                      ) : (
                        <Trans i18nKey="scaling.action.resume">Resume</Trans>
                      )}
                    </div>
                    <div
                      className={cn('t-sb text-red-500 cursor-pointer')}
                      onClick={(e) => {
                        e.stopPropagation()
                        onDeleteScaleSpec(spec)
                      }}
                    >
                      <Trans i18nKey="scaling.action.delete">Delete</Trans>
                    </div>
                  </div>
                </td>
              </CollapseTableRow>
            ))}
          </tbody>
        </table>
      )}
      {!loading && scaleSpecList.length > 0 && (
        <Pagination
          size={pageSize}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={(page) => getScaleSpecList(page)}
        />
      )}
      {!loading && scaleSpecList.length === 0 && (
        <div className="mt-4">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}
    </div>
  )
}
